import { bodyRegionValues, footageActionValues, serviceFootageOtherFieldValues, stockSupportingStepRules, stockSupportingStepValues, treatmentAreaStatusValues, } from '@borradh-workspace/labels';
import { z } from 'zod';
export const serviceTreatmentSelectionSchema = z
    .object({
    treatmentSlug: z.string().trim().min(1).max(100),
    areaStatus: z.enum(treatmentAreaStatusValues).default('unknown'),
    regions: z
        .array(z.enum(bodyRegionValues))
        .refine((a) => new Set(a).size === a.length)
        .default([]),
})
    .superRefine((v, ctx) => {
    if ((v.areaStatus === 'selected') !== v.regions.length > 0)
        ctx.addIssue({
            code: 'custom',
            path: ['regions'],
            message: 'Choose areas, or keep the area unresolved/not applicable',
        });
});
export const stockTreatmentScopeSchema = z
    .object({
    treatmentSlug: z.string().trim().min(1).max(100),
    areaStatus: z
        .enum(['unknown', 'selected', 'independent'])
        .default('unknown'),
    regions: z
        .array(z.enum(bodyRegionValues))
        .refine((a) => new Set(a).size === a.length)
        .default([]),
    procedureNeutral: z.boolean().default(false),
    shotRole: z
        .enum(['procedure', 'preparation', 'supporting', 'result'])
        .optional(),
    supportingStep: z.enum(stockSupportingStepValues).optional(),
})
    .superRefine((v, ctx) => {
    if (v.supportingStep) {
        const rule = stockSupportingStepRules[v.supportingStep];
        const allowedTreatments = rule.treatments;
        const allowedRegions = rule.regions;
        if (!allowedTreatments.includes(v.treatmentSlug) ||
            v.shotRole !== rule.shotRole ||
            v.procedureNeutral ||
            (allowedRegions.length === 0
                ? v.areaStatus !== 'independent'
                : v.areaStatus !== 'selected' ||
                    !v.regions.length ||
                    v.regions.some((r) => !allowedRegions.includes(r))))
            ctx.addIssue({
                code: 'custom',
                path: ['supportingStep'],
                message: 'Choose a supported treatment, shot role and visible area for this shared step',
            });
    }
    else if (v.shotRole && v.shotRole !== 'procedure') {
        ctx.addIssue({
            code: 'custom',
            path: ['supportingStep'],
            message: 'Supporting footage needs an explicit shared-step enum',
        });
    }
    if ((v.areaStatus === 'selected') !== v.regions.length > 0)
        ctx.addIssue({
            code: 'custom',
            path: ['regions'],
            message: 'Choose visible areas, or explicitly mark area-independent scope',
        });
});
export const treatmentCatalogueEntrySchema = z.object({
    slug: z.string(),
    displayName: z.string(),
    techniqueSlug: z.string(),
    parentSlug: z.string().nullable(),
    agentSlug: z.string().nullable(),
    kind: z.enum(['specific', 'family']),
    aliases: z.array(z.string()),
});
/** Optional service choices. An explicit Other disables stock, not uploaded footage. */
export const serviceFootageSpecRequestSchema = z
    .object({
    treatments: z
        .array(serviceTreatmentSelectionSchema)
        .max(12)
        .refine((a) => new Set(a.map((x) => x.treatmentSlug)).size === a.length)
        .default([]),
    techniqueSlug: z.string().trim().min(1).max(100).nullable().default(null),
    agentSlugs: z
        .array(z.string().trim().min(1).max(100))
        .max(8)
        .refine((a) => new Set(a).size === a.length)
        .default([]),
    regions: z
        .array(z.enum(bodyRegionValues))
        .refine((a) => new Set(a).size === a.length)
        .default([]),
    footageActions: z
        .array(z.enum(footageActionValues))
        .max(footageActionValues.length)
        .refine((a) => new Set(a).size === a.length)
        .default([]),
    footageOtherFields: z
        .array(z.enum(serviceFootageOtherFieldValues))
        .max(4)
        .refine((a) => new Set(a).size === a.length)
        .default([]),
})
    .superRefine((spec, ctx) => {
    if (spec.techniqueSlug !== null ||
        spec.agentSlugs.length ||
        (spec.regions.length && spec.treatments.length) ||
        spec.footageActions.length) {
        ctx.addIssue({
            code: 'custom',
            path: ['treatments'],
            message: 'Use the current treatment catalogue; legacy treatment/product/area/action selections are no longer accepted',
        });
    }
    const conflicts = {
        treatment: spec.techniqueSlug !== null || spec.treatments.length > 0,
        product: spec.agentSlugs.length > 0,
        body_area: spec.regions.length > 0 ||
            spec.treatments.some((t) => t.regions.length > 0),
        action: spec.footageActions.length > 0,
    };
    for (const field of spec.footageOtherFields) {
        if (conflicts[field])
            ctx.addIssue({
                code: 'custom',
                path: ['footageOtherFields'],
                message: 'Choose Other or listed options for the same field',
            });
    }
});
export const serviceTreatmentOptionsRequestSchema = z
    .object({ name: z.string().trim().min(2).max(300) })
    .strict();
export const serviceTreatmentOptionsResponseSchema = z.object({
    catalogue: z.array(treatmentCatalogueEntrySchema),
    recommendedSlugs: z.array(z.string()),
});
//# sourceMappingURL=service-footage.js.map